import { Group } from "./Group";
import { Match, Show, Switch, createEffect, createMemo, createSignal } from "solid-js";
import { AsyncList, Flexbox, Loader } from "st-app";
import { requireRoleDashboardContext } from "../RoleDashboardContext";
import { StudentEnrollmentHistoryEntry, roleDashboard as api } from "sis-app/api";
import { date } from "common";
import styles from '../sis-role-dashboard.module.css'

export function EnrollmentActivity() {
    const ctx = requireRoleDashboardContext();
    const [error, setError] = createSignal<string | null>(null);
    const [resetKey, setResetKey] = createSignal('0');

    createEffect(() => {
        ctx.refreshTrigger();
        setResetKey(prev => (prev + 1).toString());
    });

    async function loadData(page: number) {
        try {
            setError(null);
            return await api.GetEnrollmentHistory(page);
        } catch (err) {
            console.error('Failed to load enrollment history:', err);
            setError(err instanceof Error ? err.message : 'Failed to load enrollment activity');
            throw err; // Re-throw so AsyncList knows it failed
        }
    }

    const hasActivity = createMemo(() => ctx.enrActivityCount() > 0);

    return <Group title="Recent Enrollment Activity" icon="fas fa-clock" color="--header-red">
        <Show
            when={hasActivity()}
            fallback={
                <Flexbox dir="vertical" middle style="padding: 20px; text-align: center; color: var(--st-hint-fg);">
                    <Show when={error()}>
                        <div style="color: var(--st-error-fg); margin-bottom: 10px;">
                            <i class="fas fa-exclamation-triangle" style="margin-right: 5px;"></i>
                            {error()}
                        </div>
                    </Show>
                    <Show when={!error()}>
                        <div>No recent enrollment activity</div>
                    </Show>
                </Flexbox>
            }
        >
            <Show when={error()}>
                <Flexbox dir="vertical" middle style="padding: 10px; background-color: var(--st-error-bg); color: var(--st-error-fg); margin: 10px; border-radius: 5px;">
                    <div>
                        <i class="fas fa-exclamation-triangle" style="margin-right: 5px;"></i>
                        {error()}
                    </div>
                </Flexbox>
            </Show>
            <AsyncList
                key={resetKey()}
                class={styles.enrActivity}
                totalRows={ctx.enrActivityCount()}
                rowCount={6}
                loadData={loadData}
            >
                {entry =>
                    <Switch fallback={<Loader size="medium" />}>
                        <Match when={entry}>
                            <Entry {...entry} />
                        </Match>
                    </Switch>
                }
            </AsyncList>
        </Show>
    </Group>
}

function Entry(props: StudentEnrollmentHistoryEntry) {
    const entryDate = createMemo(() => date.dateFromString(props.activityDate));
    const initials = createMemo(() => {
        return (props.userName ?? '')
            .split(' ')
            .map(n => n[0])
            .join('');
    });

    const isFuture = createMemo(() => {
        const dt = date.dateFromString(props.activityDate);
        const today = date.truncateTime(new Date());

        return dt.getTime() > today.getTime();
    });

    return <Flexbox dir="horizontal" class={styles.enrHistRow} middle padded >
        <Flexbox.Item auto style="font-weight: bold">
            {props.studentName}
        </Flexbox.Item>
        <Flexbox.Item auto style="padding: 0 10px; opacity: 0.5">Grade: {props.gradeLevelDesc}</Flexbox.Item>
        <Show when={isFuture()}>
            <Flexbox.Item style="white-space: nowrap; min-width: 180px; font-style: italic">
                Future {props.enrollmentTypeDesc}
            </Flexbox.Item>
        </Show>
        <Show when={!isFuture()}>
            <Flexbox.Item style="white-space: nowrap; min-width: 180px">
                {props.enrollmentTypeDesc}
            </Flexbox.Item>
        </Show>
        <Flexbox.Item>{date.toString(entryDate())}</Flexbox.Item>
        <Flexbox.Item class={styles.userBadge} >{initials()}</Flexbox.Item>
    </Flexbox>
}
